import stationDataConfigApi from '@/api/config/stationDataConfig'
import { queryKeys } from '@/constant/common'
import { useQueries } from '@tanstack/react-query'
import {
  STATION_STATE_BUCKETS,
  STATION_STATE_BUCKET_IDS,
  type StationStateBucket,
} from '@/features/config/pages/StationTableView/station-state-buckets'
import type { StationServerFilters } from '@/features/config/pages/StationTableView/server-filters'

export type StationStatusCounts = Record<StationStateBucket, number>

const EMPTY: StationStatusCounts = {
  running: 0,
  waitStart: 0,
  error: 0,
  idling: 0,
}

/**
 * How many stations are in each status group, counted by the endpoint.
 *
 * These cannot be counted from the loaded rows: the list pages, so a freshly
 * opened controller holds twenty of its stations and counting those reports
 * "Running 0" for a controller with twenty running. One request per group,
 * asking for a single row and reading `totalCount` — the rows are thrown away,
 * only the count is wanted.
 *
 * The current filters are passed through so the counters describe the list on
 * screen. `keyword` too, for the same reason. `StateIds` is overridden per
 * group: a status filter narrows which groups have rows, and the counters
 * should then read 0 for the groups it excludes rather than ignoring it.
 */
export default function useStationStatusCounts(
  interfaceId: number | null,
  serverFilters?: StationServerFilters,
  keyword?: string,
  enabled = true,
): StationStatusCounts {
  const isEnabled = enabled && interfaceId != null

  const results = useQueries({
    queries: STATION_STATE_BUCKETS.map((bucket) => {
      // A status filter and this counter both mean StateIds; intersect them, so
      // filtering to Error leaves the other groups reading 0.
      const picked = serverFilters?.StateIds
      const ids = picked
        ? STATION_STATE_BUCKET_IDS[bucket].filter((id) => picked.includes(id))
        : STATION_STATE_BUCKET_IDS[bucket]

      return {
        queryKey: [
          queryKeys.stations,
          'status-count',
          interfaceId,
          bucket,
          keyword,
          JSON.stringify(serverFilters ?? {}),
        ],
        queryFn: async () => {
          if (ids.length === 0) return 0
          const { data } = await stationDataConfigApi.getAll({
            interfaceId: interfaceId as number,
            PageIndex: 1,
            PageSize: 1,
            ...(keyword ? { keyword } : {}),
            ...(serverFilters ?? {}),
            StateIds: ids,
          })
          return data?.totalCount ?? 0
        },
        enabled: isEnabled,
        refetchOnWindowFocus: false,
      }
    }),
  })

  if (!isEnabled) return EMPTY

  return STATION_STATE_BUCKETS.reduce((counts, bucket, index) => {
    counts[bucket] = (results[index]?.data as number) ?? 0
    return counts
  }, {} as StationStatusCounts)
}
